<template>
  <article
      class="clinic-card"
      :class="{'clinic-card--active': isActive}"
      :data-clinic-id="clinic.id"
      @click="emit('select', clinic.id)"
  >
    <div class="clinic-card__logo">
      <UIAvatar :id="clinic.id" :detailUrl="detailUrl" :fullName="clinic.name"
                :photo="clinic.logo || '/images/no-photo.webp'" :rating="clinic.rating"
                :reviewsCount="clinic.reviewCount" :add-review-button="false"
                model="clinics" preset="clinicLogo" width="72" height="72"/>
    </div>

    <div class="clinic-card__mid">
      <div class="clinic-card__intro">
        <h3 class="clinic-card__name">
          <NuxtLink :to="detailUrl">
            {{ clinic.name }}<span v-if="clinic.isProfileComplete" class="clinic-card__badge">&nbsp;<UIProfileCompleteBadge/></span>
          </NuxtLink>
          <ClinicOwnerClaimLink v-if="showOwnerClaim" :clinic-id="clinic.id"/>
        </h3>
        <p v-if="kinds.length" class="clinic-card__kinds">
          <template v-for="(kind, index) in kinds" :key="kind">
            <span v-if="index" class="clinic-card__kind-sep" aria-hidden="true">|</span>
            <span>{{ kind }}</span>
          </template>
        </p>
        <NuxtLink v-if="doctorsCountLabel" :to="`${detailUrl}#doctors`" class="clinic-card__doctors">
          <template v-if="doctorsPriceLabel">
            {{ doctorsCountLabel }} | <span class="clinic-card__doctors-priem">приём </span>от {{ doctorsPriceLabel }}
          </template>
          <template v-else>{{ doctorsCountLabel }}</template>
        </NuxtLink>
      </div>
      <ClinicFeatureIcons :codes="clinic.featureCodes"/>
      <div v-if="photos.length" class="clinic-card__photos">
        <NuxtImg v-for="photo in photos" :key="photo" :src="photo" class="clinic-card__photo" alt=""
                 width="72" height="54" fit="cover" format="webp" loading="lazy" aria-hidden="true"/>
      </div>
      <ClinicServicesSchedule v-if="clinicServices.length" :clinic="clinic" :serviceId="serviceId"
                              :services="clinicServices"/>
    </div>

    <div class="clinic-card__side">
      <p v-if="clinic.address" class="clinic-card__geo">
        <SvgoAddress aria-hidden="true"/>
        <span>
          <span v-if="distance" class="clinic-card__distance">{{ distance }} от вас</span><template
            v-if="distance"> · </template><span v-html="sanitizeHtml(clinic.address)"/>
        </span>
      </p>
      <div v-if="metroStations.length" class="clinic-card__metro">
        <div v-for="metro in metroStations" :key="metro.id" class="clinic-card__metro-item">
          <span class="clinic-card__metro-line" :style="{backgroundColor: metro.line.color}" :title="metro.line.name"/>
          <span>{{ metro.name }}</span>
          <span v-if="metro.distanceKm" class="clinic-card__metro-distance">
            {{ formatDistanceKm(metro.distanceKm) }}
          </span>
        </div>
      </div>
      <p v-if="clinic.schedule" class="clinic-card__geo" :class="{'clinic-card__geo--accent': clinic.isAlwaysOpen}">
        <SvgoTime aria-hidden="true"/>
        <span v-html="sanitizeHtml(clinicSchedulePlain(clinic.schedule))"/>
      </p>
      <div class="clinic-card__cta" @click.stop @mousedown.stop @mouseup.stop>
        <ClinicCallButton :clinic="clinic" source="list" classList="outline"/>
        <ClinicAppointmentButton :clinic="clinic" :serviceId="serviceId"/>
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import type {TClinic, TClinicService} from "~/types/clinic";
import {sanitizeHtml} from "~/utils/sanitizeHtml";
import {clinicSchedulePlain} from "~/utils/clinicScheduleLabel";
import {formatDistanceKm} from "~/utils/distance";
import {resolvePublicMediaSrc} from "~/utils/optimizableImageSrc";
import {declineWord, formatPrice} from "~/utils/number";

const METRO_STATIONS_IN_CARD = 2;
const PHOTOS_IN_CARD = 4;

const props = defineProps<{
  clinic: TClinic;
  serviceId?: number;
  /** Карточка подсвечена: клик по ней самой или по пину на карте. */
  isActive?: boolean;
}>();

const emit = defineEmits<{
  select: [clinicId: number];
}>();

const detailUrl = computed<string>(() => {
  if (!props.clinic.slug) {
    return "";
  }
  return `/clinics/${props.clinic.citySlug}/${props.clinic.slug}/`;
});

const kinds = computed<string[]>(() => props.clinic.kinds ?? []);

const showOwnerClaim = computed(() => props.clinic.hasUser === false);

const photos = computed<string[]>(() =>
    (props.clinic.photos ?? []).slice(0, PHOTOS_IN_CARD).map(resolvePublicMediaSrc));

const metroStations = computed(() => (props.clinic.metroStations ?? []).slice(0, METRO_STATIONS_IN_CARD));

const distance = computed<string>(() =>
    props.clinic.distanceKm != null ? formatDistanceKm(props.clinic.distanceKm) : "");

// «10 ветеринаров | приём от 1 500 ₽»: без врачей строки нет, без цены — только врачи.
// На xs слово «приём» прячется через CSS (.clinic-card__doctors-priem).
const doctorsCountLabel = computed<string>(() => {
  const count = props.clinic.doctorsCount ?? 0;
  if (!count) {
    return "";
  }
  return `${count} ${declineWord(count, ["ветеринар", "ветеринара", "ветеринаров"])}`;
});

const doctorsPriceLabel = computed<string>(() => {
  const price = props.clinic.appointmentPriceFrom;
  return price ? formatPrice(price) : "";
});

const clinicServices = computed<TClinicService[]>(() =>
    Array.isArray(props.clinic.services) ? props.clinic.services : []);
</script>

<style lang="scss">
.clinic-card {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 28px 32px;
  background-color: $color-white;
  border: 1.5px solid $color-hairline;
  border-radius: 32px;
  box-shadow: 0 4px 10px 0 rgb(138 138 176 / 7%);

  &:hover {
    border-color: tint($color-purple, $color-white, 18%);
  }

  &--active {
    border-color: $color-purple;
    box-shadow: 0 0 0 3px alpha($color-purple, 0.12);
  }

  &__logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 88px;
    width: 88px;
    gap: 8px;

    .avatar {
      margin-bottom: 0;
      text-align: center;
    }

    .avatar--sized {
      width: 72px;
      height: auto;
      overflow: visible;
    }

    // Avatar по умолчанию центрирует круг (margin: auto) — из-за широкого рейтинга
    // лого уезжает вправо относительно названия и адреса.
    .avatar__media {
      margin-left: 0;
      margin-right: 0;
    }

    // Макет: рейтинг под логотипом, без белой таблетки Avatar.
    .avatar__rating {
      justify-content: center;
      margin: 8px 0 0;
      padding: 0;
      background: none;
      box-shadow: none;
      border-radius: 0;
      height: auto;
    }

    .rating-widget {
      flex-wrap: nowrap;
      white-space: nowrap;
      font-size: 13px;
    }

    .rating-widget-item--reviews .rating-icon {
      display: none;
    }

    .rating-widget-item--reviews .rating-value::before {
      content: "· ";
    }
  }

  &__mid {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }

  &__intro {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__name {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
    min-width: 0;

    .clinic-owner-claim {
      margin-left: 8px;
      vertical-align: middle;
    }

    a:not(.clinic-owner-claim) {
      color: $color-black;
      text-decoration: none;

      &:hover {
        color: $color-accent-text;
      }
    }
  }

  &__badge {
    white-space: nowrap;

    svg {
      vertical-align: middle;
    }
  }

  &__kinds {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 6px;
    margin: 0;
    color: $color-gray;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
  }

  &__kind-sep {
    color: $color-text-subtle;
    font-weight: 400;
  }

  &__doctors {
    width: fit-content;
    color: $color-accent-text;
    font-size: 15px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__doctors-priem {
    display: inline;
  }

  &__photos {
    display: flex;
    gap: 8px;
  }

  &__photo {
    width: 72px;
    height: 54px;
    flex-shrink: 0;
    border-radius: 10px;
    background-color: $color-dark-white;
    object-fit: cover;
  }

  &__side {
    display: flex;
    flex: 0 0 248px;
    flex-direction: column;
    gap: 10px;
    width: 248px;
  }

  &__geo {
    display: flex;
    gap: 8px;
    margin: 0;
    color: $color-gray;
    font-size: 14px;
    line-height: 1.35;

    svg {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 2px;
      color: $color-accent-text;
    }

    > span {
      white-space: pre-line;
      @include lineClamp(3);
    }

    &--accent {
      color: $color-accent-text;
      font-weight: 500;
    }
  }

  &__distance {
    color: $color-accent-text;
    font-weight: 500;
  }

  &__metro {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
  }

  &__metro-item {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: nowrap;
    gap: 6px;
    color: $color-gray;
    font-size: 14px;
  }

  &__metro-line {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    border-radius: 50%;
  }

  &__cta {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 12px;
    margin-top: 4px;
    padding-top: 0;

    .button {
      flex: 1 1 0;
      width: auto;
      min-width: 0;
      height: 40px;
      padding: 0 10px;
      font-size: 14px;
      line-height: 1.15;
      white-space: nowrap;
    }

    .button.outline {
      background-color: $color-pink;
      border-color: transparent;
      color: $color-accent-text;
    }

    .clinic-call-button + .clinic-appointment-button {
      min-width: max-content;
    }

    // Раскрытый номер на десктопе: тот же рост, что у «Записаться», с воздухом внутри.
    .clinic-call-button {
      flex: 1 0 auto;
      box-sizing: border-box;
      height: 40px;
      padding: 8px 14px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .clinic-call-button__icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
    }
  }

  // Мобилка: лого+рейтинг слева, название/тип/врачи справа в той же строке;
  // контакты и CTA — на всю ширину под шапкой (как в макете VTS-2288).
  @include media(sm) {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    grid-template-areas:
      "logo mid";
    align-items: start;
    gap: 12px 14px;
    padding: 16px;
    border-radius: 32px;

    &__logo {
      grid-area: logo;
      flex: none;
      width: 72px;
      align-items: flex-start;

      .avatar {
        text-align: left;
      }

      .avatar--sized {
        width: 72px;
        height: auto;
      }

      .avatar__media,
      .avatar__image,
      .avatar__image img,
      .avatar__initials {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        overflow: hidden;
      }

      .avatar__rating {
        justify-content: flex-start;
        margin-top: 10px;
      }
    }

    &__mid {
      display: contents;

      > :not(.clinic-card__intro) {
        grid-column: 1 / -1;
      }
    }

    &__intro {
      grid-area: mid;
    }

    &__side {
      grid-column: 1 / -1;
      flex: none;
      gap: 12px;
      width: auto;
    }

    &__name {
      font-size: 18px;
    }

    &__kinds {
      font-size: 13px;
    }

    &__doctors {
      font-size: 13px;
      white-space: nowrap;
    }

    // Узкая колонка справа от лого: «приём от» → «от».
    &__doctors-priem {
      display: none;
    }

    &__photos {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 72px));
    }

    &__photo {
      width: 100%;
      height: auto;
      aspect-ratio: 4 / 3;
    }

    &__metro-item:nth-child(n + 3) {
      display: none;
    }

    &__cta {
      padding-top: 0;
      margin-top: 4px;
    }

    &__cta .button {
      width: auto;
      padding: 0 8px;
      font-size: 14px;
    }
  }
}
</style>
